import React, { useEffect, useMemo, useState } from 'react';
import RefreshOutlined from '@mui/icons-material/RefreshOutlined';
import PictureAsPdfOutlined from '@mui/icons-material/PictureAsPdfOutlined';
import { toast } from 'react-toastify';
import { Modal, Button, Select, Alert, DataTable } from '../../../components/ui';
import { LOCATIONS, PPM_TYPE_OPTIONS } from '../QDFConstants';
import { buildPendingPpmReport, formatPpmReportDate } from '../utils/pendingPpmReport';
import { fetchPendingPpmEntries, exportPendingPpmPdf } from '../services/pendingPpmReport';

const summaryColumns = [
  { key: 'location', header: 'Location', primary: true },
  { key: 'ppmType', header: 'PPM type' },
  { key: 'pending', header: 'Pending' },
  { key: 'overdue', header: 'Overdue' },
  { key: 'onHold', header: 'On hold' },
  { key: 'noDueDate', header: 'No due date' },
];

const PendingPpmReportModal = ({ onClose, onView }) => {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [filters, setFilters] = useState({ location: '', ppmType: '', timing: '' });

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    setSnapshot(null);
    fetchPendingPpmEntries().then((entries) => {
      if (active) setSnapshot({ entries, asOf: new Date().toISOString() });
    }).catch((err) => {
      console.error('Error fetching pending PPM report:', err);
      if (active) setError('Failed to load the full PPM report. Please retry.');
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [refresh]);

  const report = useMemo(() => (
    snapshot ? buildPendingPpmReport(snapshot.entries, snapshot.asOf, filters) : null
  ), [snapshot, filters]);

  const changeFilter = (key) => (event) => {
    setFilters((previous) => ({ ...previous, [key]: event.target.value }));
  };

  const exportPdf = () => {
    try {
      exportPendingPpmPdf(report);
    } catch (err) {
      console.error('Pending PPM PDF export error:', err);
      toast.error('Failed to export pending PPM report');
    }
  };

  const detailColumns = [
    { key: 'scNumber', header: 'SC number', primary: true },
    { key: 'place', header: 'Location / zone' },
    { key: 'assetNumber', header: 'Asset', render: (row) => row.assetNumber || row.assetTag || '-' },
    { key: 'ppmType', header: 'PPM type' },
    { key: 'creationDate', header: 'Scheduled', render: (row) => formatPpmReportDate(row.creationDate) },
    { key: 'targetResolution', header: 'Target resolution', render: (row) => formatPpmReportDate(row.targetResolution) },
    { key: 'scStatus', header: 'Status' },
    { key: 'timing', header: 'Deadline', render: (row) => (
      <span className={row.overdue ? 'font-semibold text-danger' : ''}>
        {row.timing}{row.overdue ? ` (${row.daysOverdue}d overdue)` : ''}
      </span>
    ) },
    { key: 'contractor', header: 'Contractor', render: (row) => row.contractor || 'Not set' },
    { key: 'action', header: 'Action', render: (row) => (
      <Button variant="ghost" size="sm" onClick={() => onView(row)}>View work order</Button>
    ) },
  ];

  return (
    <Modal isOpen onClose={onClose} size="xl" title="Pending PPM report"
      subtitle="Consolidated preventive maintenance backlog - all dates"
      footer={<>
        <Button variant="ghost" onClick={onClose}>Close</Button>
        <Button variant="secondary" icon={RefreshOutlined} disabled={loading}
          onClick={() => setRefresh((value) => value + 1)}>Refresh</Button>
        <Button icon={PictureAsPdfOutlined} disabled={!report || loading} onClick={exportPdf}>Export PDF</Button>
      </>}>
      <div className="space-y-5">
        <p className="text-sm text-muted-fg">
          Active and On Hold PPM only; Closed, Cancelled and service requests are excluded.
          Overdue means the target resolution time has passed; days overdue are rounded up.
          Only existing work orders are included, not schedules that have not been generated.
        </p>
        <div className="grid gap-3 sm:grid-cols-3">
          <Select label="Location" value={filters.location} onChange={changeFilter('location')}>
            <option value="">All locations</option>
            {LOCATIONS.map((value) => <option key={value}>{value}</option>)}
          </Select>
          <Select label="PPM type" value={filters.ppmType} onChange={changeFilter('ppmType')}>
            <option value="">All PPM types</option>
            {PPM_TYPE_OPTIONS.map((value) => <option key={value}>{value}</option>)}
          </Select>
          <Select label="Deadline" value={filters.timing} onChange={changeFilter('timing')}>
            <option value="">All deadlines</option>
            {['Overdue', 'Within deadline', 'No due date'].map((value) => <option key={value}>{value}</option>)}
          </Select>
        </div>
        {loading && <p role="status">Loading all PPM work orders...</p>}
        {error && <Alert variant="error">{error}</Alert>}
        {report && !loading && <>
          <p className="text-xs text-muted-fg">As of {formatPpmReportDate(report.asOf)}. Refresh for the latest status. Totals reflect the selected filters.</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              ['Pending', report.pending], ['Overdue', report.overdue],
              ['On hold', report.onHold], ['No due date', report.noDueDate],
            ].map(([label, count]) => (
              <div key={label} className="rounded-lg border border-border bg-surface p-4">
                <p className="text-sm text-muted-fg">{label}</p>
                <p className="text-2xl font-semibold text-foreground">{count}</p>
              </div>
            ))}
          </div>
          <section>
            <h3 className="mb-3 text-sm font-semibold">Summary by location and PPM type</h3>
            <DataTable columns={summaryColumns} rows={report.groups} keyField="key"
              emptyTitle="No pending PPM matches these filters" />
          </section>
          <section>
            <h3 className="mb-3 text-sm font-semibold">Pending work orders - overdue first</h3>
            <div className="overflow-x-auto">
              <DataTable columns={detailColumns} rows={report.rows}
                className="md:min-w-[1400px]"
                emptyTitle="No pending PPM matches these filters" />
            </div>
          </section>
        </>}
      </div>
    </Modal>
  );
};

export default PendingPpmReportModal;
